'use client'

import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'
import PaginationProducts from '@/components/global/pagination'
import { VideoClassFilterButtonLoader } from '@/components/global/video-class-filter-button-loader'
import FeaturedProductCard from '@/components/template-one/cursos/featured-product-card'
import { ProductsNotFoundWarn } from '@/components/template-one/global/products-not-found-warn'
import { Button } from '@/components/ui/button'
import {
  useFeaturedProduct,
  useProductsCatalog,
} from '@/features/products-catalog/hooks/use-products-catalog'
import { buildCatalogQuery } from '@/features/products-catalog/search-params'
import type { FilterableProductType } from '@/helpers/products-filter-type'
import ProductCardGroup from './product-card-group'
import SelectSort from './select-sort'

const GRID_COLUMNS = 3

const FEATURED_TITLE_BY_PRODUCT_TYPE: Partial<
  Record<FilterableProductType, string>
> = {
  postgraduate: 'Pós-graduações em ',
  track: 'Trilhas em ',
  webinar: 'Webinars em ',
}

interface ProductsCatalogProps {
  productType: FilterableProductType
  template: string | null
  areaSlug?: string
  categorySlug?: string
}

export function ProductsCatalog({
  productType,
  template,
  areaSlug = '',
  categorySlug = '',
}: ProductsCatalogProps) {
  const searchParams = useSearchParams()
  const query = useMemo(
    () =>
      buildCatalogQuery({ searchParams, productType, areaSlug, categorySlug }),
    [searchParams, productType, areaSlug, categorySlug]
  )

  const { data, isPending, isError, isPlaceholderData, refetch } =
    useProductsCatalog(query)
  const featured = useFeaturedProduct(data?.links?.area_highlighted_product)

  const products = data?.data ?? []
  const pagination = data?.pagination
  const totalPages = pagination?.total_pages ?? 0
  const hasProducts = products.length > 0
  const isLoading = isPending || isPlaceholderData

  return (
    <>
      <div className="w-full">
        <header className="flex flex-wrap items-end md:justify-between">
          <div className="leading-6">
            {hasProducts || isLoading ? (
              <h2 className="font-bold text-2.5xl text-neutral-800">
                {FEATURED_TITLE_BY_PRODUCT_TYPE[productType] ?? 'Cursos em '}
                <span className="text-primary-700">destaque</span>
              </h2>
            ) : (
              <h2 className="font-bold text-2.5xl text-neutral-800">
                Sem <span className="text-pink-600">registros</span>
              </h2>
            )}

            <small className="font-normal text-gray-500 text-sm">
              {pagination?.total_results ?? 0} Resultados
            </small>
          </div>
          <div className="mt-4 flex w-full flex-wrap items-center gap-2 md:mt-0 md:justify-end">
            <span className="hidden w-24 font-normal text-gray-500 text-sm md:block">
              Organizar por:
            </span>
            <div className="hidden md:block md:w-max" id="order">
              <SelectSort />
            </div>

            <VideoClassFilterButtonLoader template={template} />
          </div>
        </header>

        {featured.data && (
          <div className="mt-6 mb-4 lg:mb-12">
            <FeaturedProductCard product={featured.data} />
          </div>
        )}

        {isError ? (
          <div
            className="mt-6 flex flex-col items-center gap-3 rounded-lg border border-pink-200 bg-pink-50 p-6 text-center"
            role="alert"
          >
            <p className="font-medium text-gray-700">
              Não foi possível carregar os cursos agora.
            </p>
            <Button onClick={() => refetch()} type="button" variant="outline">
              Tentar novamente
            </Button>
          </div>
        ) : (
          <ProductCardGroup
            columns={GRID_COLUMNS}
            isLoading={isLoading}
            products={products}
          />
        )}

        {!(hasProducts || isLoading || isError) && (
          <ProductsNotFoundWarn
            area={areaSlug}
            category={categorySlug}
            searchTerm={query.search}
          />
        )}
      </div>

      {totalPages > 1 && pagination && (
        <div className="flex flex-wrap items-center justify-center gap-y-3 lg:justify-between">
          <span className="font-medium text-gray-500 text-xs">
            Exibindo {pagination.current_page} - {totalPages} de {totalPages}{' '}
            resultados
          </span>
          <PaginationProducts total={totalPages} />
        </div>
      )}
    </>
  )
}
